import React, { Dispatch, RefObject, MutableRefObject, useMemo, useCallback } from 'react';
import { Checkbox, SxProps, TableCell, TableHead, TableRow } from '@mui/material';
import { classes } from './styles';
import { handleSelectAllOnPage, handleScrollableEnter, handleScrollableExit } from './tableHelper';
import {
  GenericTableData,
  TPaginationState,
  TSortableColumn,
  TTableState,
  TablePaginationAction,
  TableSelectionAction,
  TableScrollAction
} from './types';
import { updateUiSettings } from '../../helpers/uiPreferencesHelpers';
import TooltipTableCell from './TooltipTableCell';

interface IGenericTableHeadProps<T> {
  tableState: TTableState<T, TSortableColumn[]>;
  paginationState: TPaginationState<string>;
  dispatch: Dispatch<TableSelectionAction<T> | TablePaginationAction<string> | TableScrollAction>;
  items: T[];
  titleCellRef: RefObject<HTMLTableCellElement>;
  actionCellRef: RefObject<HTMLTableCellElement>;
  headerStyle: (id: string, tableHovered: boolean) => SxProps;
  titleCellId?: string;
  userPreferenceSource?: string;
  hasActionItems?: boolean;
  allowMultipleSelection?: boolean;
  customRowStyles?: Record<string, SxProps>;
  tableRef: RefObject<HTMLDivElement>;
  hoverTimeoutRef: MutableRefObject<NodeJS.Timeout | null>;
  isHoveringRef: MutableRefObject<boolean>;
}

const GenericTableHead = <GenericTableHeadProps extends GenericTableData>({
  tableState,
  paginationState,
  dispatch,
  items,
  titleCellRef,
  actionCellRef,
  headerStyle,
  titleCellId = 'name',
  userPreferenceSource,
  hasActionItems = true,
  allowMultipleSelection = true,
  customRowStyles,
  tableRef,
  hoverTimeoutRef,
  isHoveringRef
}: IGenericTableHeadProps<GenericTableHeadProps>) => {
  const selectAllIsIndeterminate = useMemo(
    () => items.map((item) => item.id).some((id) => tableState.selectedItemIds.includes(id)),
    [items, tableState.selectedItemIds]
  );
  const selectAllIsChecked = useMemo(
    () => items.map((item) => item.id).every((id) => tableState.selectedItemIds.includes(id)),
    [items, tableState.selectedItemIds]
  );

  const handleSortChange = (id: string) => {
    const sortOrder =
      paginationState.sort === id && paginationState.sort_order === 'asc' ? 'desc' : 'asc';
    dispatch({ type: 'SET_SORT', payload: { sortBy: id, sortOrder } });
    if (userPreferenceSource) {
      updateUiSettings({
        source: userPreferenceSource,
        sorting: {
          sortBy: id,
          sortOrder
        }
      });
    }
  };

  const clearHoverTimeout = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const handleRowMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      if (!titleCellRef.current || !actionCellRef.current) return;

      const titleRect = titleCellRef.current.getBoundingClientRect();
      const actionRect = actionCellRef.current.getBoundingClientRect();
      const mouseX = event.clientX;

      if (mouseX > titleRect.right && mouseX < actionRect.left) {
        clearHoverTimeout();
        isHoveringRef.current = true;
        handleScrollableEnter(tableRef, dispatch);
      } else {
        isHoveringRef.current = false;
        handleScrollableExit(dispatch);
      }
    },
    [tableRef, dispatch, titleCellRef, actionCellRef, clearHoverTimeout]
  );

  const handleRowMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      const relatedTarget = event.relatedTarget as HTMLElement;
      if (
        relatedTarget &&
        (relatedTarget.id === 'left-arrow' ||
          relatedTarget.id === 'right-arrow' ||
          relatedTarget.closest('#left-arrow') ||
          relatedTarget.closest('#right-arrow'))
      ) {
        return;
      }
      isHoveringRef.current = false;
      clearHoverTimeout();
      hoverTimeoutRef.current = setTimeout(() => {
        if (!isHoveringRef.current) {
          handleScrollableExit(dispatch);
        }
        hoverTimeoutRef.current = null;
      }, 400);
    },
    [dispatch, clearHoverTimeout, hoverTimeoutRef, isHoveringRef]
  );

  return (
    <TableHead>
      <TableRow
        sx={customRowStyles}
        onMouseEnter={handleRowMouseEnter}
        onMouseLeave={handleRowMouseLeave}
      >
        {allowMultipleSelection && (
          <TableCell sx={headerStyle('checkbox', tableState.tableHovered)}>
            <Checkbox
              id="select-all-checkbox"
              sx={classes.checkbox}
              indeterminate={selectAllIsIndeterminate && !selectAllIsChecked}
              checked={selectAllIsChecked}
              onChange={(e) => handleSelectAllOnPage(e, items, dispatch)}
              inputProps={{
                'aria-label': 'select all items on page'
              }}
            />
          </TableCell>
        )}
        {tableState.columns.map((header) => {
          const baseStyles = headerStyle(header.id, tableState.tableHovered);
          const baseStylesObj = baseStyles as Record<string, any>;
          const isSticky =
            allowMultipleSelection &&
            baseStylesObj &&
            baseStylesObj.position === 'sticky' &&
            baseStylesObj.left;
          let adjustedStyles = baseStyles;
          if (isSticky && baseStylesObj.left) {
            const baseLeft = baseStylesObj.left;
            const adjustedLeft =
              typeof baseLeft === 'number' ? baseLeft + 58 : `calc(${baseLeft} + 58px)`;
            adjustedStyles = { ...baseStylesObj, left: adjustedLeft } as typeof baseStyles;
          }

          return (
            <TooltipTableCell
              key={header.id}
              header={header}
              paginationState={paginationState}
              onSortChange={handleSortChange}
              ref={header.id === titleCellId ? titleCellRef : undefined}
              sx={adjustedStyles}
            />
          );
        })}
        <TableCell
          sx={{
            ...headerStyle('actions', tableState.tableHovered),
            ...(!hasActionItems && { minWidth: '20px', width: '20px' })
          }}
          ref={actionCellRef}
        />
      </TableRow>
    </TableHead>
  );
};

export default GenericTableHead;
